'use client'

import React, { useState, useEffect } from 'react'
import { useSession } from 'next-auth/react'
import { Button } from '@/app/components/common/Button'
import { Input } from '@/app/components/common/Input'
import { Header } from '@/app/components/common/Header'
import { logError, debug } from '@/lib/logging'

interface ContactFormProps {
  providerId: string
  providerName: string
  providerEmail?: string
  orgId?: string
  consultationType: 'free' | 'paid'
  packageName?: string
  paymentId?: string
  referralSource?: string
  referrer?: string
  slotStart?: string
  slotEnd?: string
  hideTimeDisplay?: boolean
  onBack?: () => void
}

interface ContactData {
  name: string
  email: string
  phone: string
  message: string
  providerId: string
  providerName: string
  providerEmail?: string
  orgId?: string
  consultationType: 'free' | 'paid'
  packageName?: string
  paymentId?: string
  referralSource?: string
  referrer?: string
}

export function ContactForm({
  providerId,
  providerName,
  providerEmail,
  orgId,
  consultationType,
  packageName,
  paymentId,
  referralSource,
  referrer,
  slotStart,
  slotEnd,
  hideTimeDisplay = false,
  onBack,
}: ContactFormProps) {
  const { data: session } = useSession()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [loadingUserData, setLoadingUserData] = useState(false)

  // Pre-fill form data from user session when logged in
  useEffect(() => {
    if (session?.user) {
      const loadUserData = async () => {
        try {
          setLoadingUserData(true)
          debug('ui', 'Loading user profile data for contact form pre-fill')

          const response = await fetch('/api/user/profile')
          if (response.ok) {
            const userData = await response.json()
            setFormData({
              name: userData.name || session.user.name || '',
              email: userData.email || session.user.email || '',
              phone: userData.phone || '',
              message: getDefaultMessage(),
            })
            debug('ui', 'Pre-filled contact form with user data')
          } else {
            // Fallback to session data
            setFormData({
              name: session.user.name || '',
              email: session.user.email || '',
              phone: '',
              message: getDefaultMessage(),
            })
          }
        } catch (error) {
          logError('Failed to load user data for contact form pre-fill', error)
          debug('ui', 'Failed to load user data, using session fallback:', error)
          // Fallback to session data
          setFormData({
            name: session.user.name || '',
            email: session.user.email || '',
            phone: '',
            message: getDefaultMessage(),
          })
        } finally {
          setLoadingUserData(false)
        }
      }

      loadUserData()
    } else {
      // Set default message for non-logged-in users
      setFormData((prev) => ({
        ...prev,
        message: getDefaultMessage(),
      }))
    }
  }, [session, consultationType, packageName])

  const getDefaultMessage = () => {
    if (consultationType === 'free') {
      return `Hi ${providerName},\n\nI'm interested in scheduling a free consultation to discuss your doula services. Please let me know your availability.\n\nThank you!`
    } else {
      return `Hi ${providerName},\n\nI've purchased your ${packageName || 'package'} and would like to schedule my appointment. Please let me know your availability.\n\nThank you!`
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    // Validate phone
    const phoneRegex = /^[\d\s\-\(\)\+]+$/
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!phoneRegex.test(formData.phone) || formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Please enter a valid phone number'
    }

    // Validate message
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide a more detailed message'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setSubmitting(true)
    setErrors({})

    const contactData: ContactData = {
      ...formData,
      providerId,
      providerName,
      providerEmail,
      orgId,
      consultationType,
      packageName,
      paymentId,
      referralSource,
      referrer,
    }

    try {
      debug('ui', 'Submitting contact form:', contactData)

      // If we have time slots, book an appointment directly
      if (slotStart && slotEnd) {
        const appointmentData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          providerId,
          orgId,
          slotStart,
          slotEnd,
          referralSource,
          referrer,
          notes: formData.message,
        }

        const response = await fetch('/api/appointments', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(appointmentData),
        })

        const result = await response.json()

        if (!response.ok) {
          throw new Error(result.error || 'Failed to book appointment')
        }

        debug('ui', 'Appointment booked successfully:', result)
        setSubmitted(true)
        return
      }

      // Otherwise, send regular contact form
      const response = await fetch('/api/contact/provider', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message')
      }

      setSubmitted(true)
      debug('ui', 'Contact form submitted successfully')
    } catch (error) {
      logError('Failed to submit contact form:', error)
      setErrors({
        submit:
          error instanceof Error ? error.message : 'Failed to send message. Please try again.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  if (submitted) {
    return (
      <div data-component-name="ContactForm" className="text-center py-8">
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <Header className="text-green-800 mb-2">Message Sent Successfully!</Header>
          <p className="text-gray-600 mb-4">
            Your message has been sent to {providerName}. They will contact you directly at{' '}
            {formData.email} to schedule your{' '}
            {consultationType === 'free' ? 'free consultation' : 'appointment'}.
          </p>
          <p className="text-sm text-gray-500">
            Please check your email (including spam folder) for their response.
          </p>
        </div>
      </div>
    )
  }

  return (
    <form data-component-name="ContactForm" onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-6">
        <Header level={3} className="mb-2">{`Contact ${providerName}`}</Header>
        <p className="text-sm text-gray-600">
          {consultationType === 'free'
            ? 'Send a message to schedule your free consultation.'
            : `Contact ${providerName} to schedule your ${packageName || 'appointment'}.`}
        </p>
      </div>

      {onBack && (
        <div className="mb-4">
          <Button type="button" variant="outline" onClick={onBack}>
            ← Back to Packages
          </Button>
        </div>
      )}

      {paymentId && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <Header
            level={4}
            className="text-green-800 mb-2"
          >{`Package Purchased: ${packageName}`}</Header>
          <p className="text-sm text-green-700">
            Your payment has been processed. Use this form to schedule your appointment with{' '}
            {providerName}.
          </p>
        </div>
      )}

      <Input
        label="Full Name"
        name="name"
        value={formData.name}
        onChange={handleInputChange}
        required
        error={errors.name}
        placeholder="Jane Smith"
        fullWidth
      />

      <Input
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleInputChange}
        required
        error={errors.email}
        placeholder="jane@example.com"
        fullWidth
      />

      <Input
        label="Phone Number"
        name="phone"
        type="tel"
        value={formData.phone}
        onChange={handleInputChange}
        required
        error={errors.phone}
        placeholder="(555) 123-4567"
        fullWidth
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          required
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
          placeholder="Tell the provider about your needs and preferences..."
        />
        {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
      </div>

      {errors.submit && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{errors.submit}</div>
      )}

      <div className="pt-4">
        <p className="text-xs text-gray-500 mb-4">
          By sending this message, you agree to receive email communications from {providerName}.
          Your information will be kept confidential and secure.
        </p>

        <Button type="submit" fullWidth disabled={submitting || loadingUserData}>
          {submitting
            ? 'Sending...'
            : loadingUserData
              ? 'Loading...'
              : consultationType === 'free'
                ? 'Send Message for Free Consultation'
                : 'Send Message to Schedule Appointment'}
        </Button>
      </div>
    </form>
  )
}
